import HeightPoint from './heights-point';
import { calcPointsHeight } from '../calc-points-height';
import { Point, Polygon, Segment } from '@flatten-js/core';
import { degreesToRadians } from '../../configurator/helpers';

export default class HeightsArea {
  points = [];
  cutouts = [];
  heightPoints = [];
  areaCfIndex;
  _slopeStartPoint;
  _slope;
  _slopeDefined;
  _slopeUserSelection;

  constructor(json = {}, potentialCutouts = []) {
    ({
      points: this.points,
      initialMinHeight: this.initialMinHeight,
      area_cf_index: this.areaCfIndex,
      slope: this._slope,
      parent: this.editState,
    } = json);
    this.initCutouts(potentialCutouts);
    this.initPoints(json);
  }

  initPoints(json) {
    if (json.heightpoints) {
      for (const pointJson of json.heightpoints) {
        this.heightPoints.push(
          new HeightPoint({ ...pointJson, parent: this, finished: true, initialMinHeight: this.initialMinHeight })
        );
      }
    } else {
      for (const point of this.points) {
        this.heightPoints.push(
          new HeightPoint({
            ...point,
            parent: this,
            finished: true,
            height: point.height ?? this.initialMinHeight,
            initialMinHeight: this.initialMinHeight,
          })
        );
      }

      for (const cutout of this.cutouts) {
        for (const cutoutPoint of cutout.points) {
          this.heightPoints.push(
            new HeightPoint({
              ...cutoutPoint,
              parent: this,
              finished: true,
              height: cutoutPoint.height ?? this.initialMinHeight,
              initialMinHeight: this.initialMinHeight,
            })
          );
        }
      }
    }
  }

  initCutouts(potentialCutouts) {
    const polygon = this.flattenPolygon;

    for (const rawCutout of potentialCutouts) {
      const firstPoint = rawCutout.points[0];
      const flattenPoint = new Point(firstPoint.x, firstPoint.y);
      if (polygon.contains(flattenPoint)) {
        this.cutouts.push({ points: rawCutout.points, area_cf_index: rawCutout.area_cf_index });
      }
    }
  }

  validate() {
    if (this.points.length === 0) {
      return false;
    }

    if (this.heightPoints.filter(point => !point.height).length) {
      return false;
    }

    return true;
  }

  get isValid() {
    return this.validate();
  }

  get shapePoints() {
    return [...this.points, this.points[0]];
  }

  getSnapEdgePoint(globalPosition, snapGlobalDistance) {
    const flattenPoint = new Point(globalPosition.x, globalPosition.y);
    const segments = [...this.flattenSegments, ...this.flattenSegmentsOfCutouts];
    let minDistanceResult; // [distance, shortest segment]
    for (const segment of segments) {
      const distanceResult = flattenPoint.distanceTo(segment);
      if (!minDistanceResult || minDistanceResult[0] > distanceResult[0]) {
        minDistanceResult = distanceResult;
      }
    }

    if (minDistanceResult[0] <= snapGlobalDistance) {
      return minDistanceResult[1].pe;
    }

    return false;
  }

  getSnapInitialPoint(globalPosition, snapGlobalDistance) {
    const flattenPoint = new Point(globalPosition.x, globalPosition.y);
    for (const point of this.allInitialPoints) {
      const flattenInitialPoint = new Point(point.x, point.y);
      const distanceResult = flattenPoint.distanceTo(flattenInitialPoint);
      if (distanceResult[0] <= snapGlobalDistance) {
        return flattenInitialPoint;
      }
    }

    return false;
  }

  getSnapSmartGuide(globalPoisition, snapGlobalDistance) {
    const numberOfClosestPoints = 5;
    const minVal = -1000000000;
    const maxVal = 1000000000;
    const horizontalSegments = [];
    const verticalSegments = [];
    const globalPoint = new Point(globalPoisition.x, globalPoisition.y);
    const pointsSortedByDistance = [...this.heightPoints].sort((a, b) => {
      const pointA = new Point(a.x, a.y);
      const pointB = new Point(b.x, b.y);
      return pointA.distanceTo(globalPoint)[0] - pointB.distanceTo(globalPoint)[0];
    });
    const closestPoints = pointsSortedByDistance.slice(
      0,
      pointsSortedByDistance.length > numberOfClosestPoints ? numberOfClosestPoints : pointsSortedByDistance.length
    );
    for (const point of closestPoints) {
      horizontalSegments.push(new Segment(new Point(minVal, point.y), new Point(maxVal, point.y)));
      verticalSegments.push(new Segment(new Point(point.x, minVal), new Point(point.x, maxVal)));
    }

    let minDistanceResult; // [distance, shortest segment]

    // Find if some intersection of edges and virtual segments applies
    for (const virtualSegment of [...horizontalSegments, ...verticalSegments]) {
      for (const edgeSegment of [...this.flattenSegments, ...this.flattenSegmentsOfCutouts]) {
        const intersections = virtualSegment.intersect(edgeSegment);
        if (intersections.length) {
          const distanceResult = intersections[0].distanceTo(globalPoint);
          if (!minDistanceResult || minDistanceResult[0] > distanceResult[0]) {
            minDistanceResult = distanceResult;
          }
        }
      }
    }
    if (minDistanceResult[0] < snapGlobalDistance) {
      return minDistanceResult[1].ps;
    }

    // Find if some of the virtual segment intersections applies
    for (const verticalSegment of verticalSegments) {
      for (const horizontalSegment of horizontalSegments) {
        const intersections = verticalSegment.intersect(horizontalSegment);
        if (intersections.length) {
          const distanceResult = intersections[0].distanceTo(globalPoint);
          if (!minDistanceResult || minDistanceResult[0] > distanceResult[0]) {
            minDistanceResult = distanceResult;
          }
        }
      }
    }
    if (minDistanceResult[0] < snapGlobalDistance) {
      return minDistanceResult[1].ps;
    }

    // Find if snap to edges applies
    const edgeSnapPoint = this.getSnapEdgePoint(globalPoisition, snapGlobalDistance);
    if (edgeSnapPoint) {
      return edgeSnapPoint;
    }

    // Find the closest minimal distance from the virtual horizontal/vertical segments
    for (const segment of [...horizontalSegments, ...verticalSegments]) {
      const distanceResult = segment.distanceTo(globalPoint);
      if (!minDistanceResult || minDistanceResult[0] > distanceResult[0]) {
        minDistanceResult = distanceResult;
      }
    }

    if (minDistanceResult[0] < snapGlobalDistance) {
      return minDistanceResult[1].ps;
    }

    return false;
  }

  get flattenPolygon() {
    const polygon = new Polygon();
    polygon.addFace(this.flattenSegments);

    return polygon;
  }

  get flattenSegments() {
    return this._getFlattenSegmentsFromPoints(this.shapePoints);
  }

  get flattenSegmentsOfCutouts() {
    const segments = [];
    for (const cutout of this.cutouts) {
      const closedPoints = [...cutout.points, cutout.points[0]];
      segments.push(this._getFlattenSegmentsFromPoints(closedPoints));
    }
    return segments.flat();
  }

  _getFlattenSegmentsOfEdges() {
    const flattenPoint = new Point(globalPosition.x, globalPosition.y);
    const segments = [...this.flattenSegments, ...this.flattenSegmentsOfCutouts];
  }

  _getFlattenSegmentsFromPoints(closedPoints) {
    const segments = [];
    for (let i = 0; i < closedPoints.length - 1; i++) {
      segments.push(
        new Segment(
          new Point(closedPoints[i].x, closedPoints[i].y),
          new Point(closedPoints[i + 1].x, closedPoints[i + 1].y)
        )
      );
    }

    return segments;
  }

  addPoint(point) {
    const heightPoint = new HeightPoint({
      ...point,
      is_initial: false,
      parent: this,
      finished: false,
      initialMinHeight: this.initialMinHeight,
    });
    this.heightPoints.push(heightPoint);
    return heightPoint;
  }

  removePoint(heightPoint) {
    if (heightPoint.isInitial) return;
    if (this.editState.isThePointLastOneWithMinHeight(heightPoint)) {
      alert(tp_trans.trans('cannot_remove_last_min_height_point', 'cannot_remove_last_min_height_point'));
      return;
    }

    const index = this.heightPoints.indexOf(heightPoint);
    this.heightPoints.splice(index, 1);
  }

  confirmSlope() {
    this.allExceptSlopeStart.forEach(point => (point.height = point.tempHeight));
    this._slopeDefined = true;
    this._slopeStartPoint = null;
  }

  calculateTempHeights(slopeEndPoint) {
    // internally the coordinate system is in CM, but we need all in MM for the calcPointsHeight function
    const cm2MmRatio = 10;
    const toBeCalculated = this.allExceptSlopeStart.map(point => point.flattenPointInMm);
    const startPoint = this.slopeStartPoint.flattenPointInMm;
    const reference = { point: startPoint, height: this.slopeStartPoint.height };
    const vectorEnd = new Point(slopeEndPoint.x * cm2MmRatio, slopeEndPoint.y * cm2MmRatio);
    if (startPoint.equalTo(vectorEnd)) {
      return;
    }
    const tempHeights = calcPointsHeight(toBeCalculated, reference, vectorEnd, degreesToRadians(this.slope));
    for (let i = 0; i < tempHeights.length; i++) {
      this.allExceptSlopeStart[i].tempHeight = Math.round(tempHeights[i]);
    }
  }

  get minHeight() {
    return Math.min(...this.heightPoints.map(point => point.height).filter(height => height > 0));
  }

  get maxHeight() {
    return Math.max(...this.heightPoints.map(point => point.height).filter(height => height > 0));
  }

  get minHeightPoints() {
    return this.heightPoints.filter(point => point.height == this.minHeight);
  }

  get allExceptSlopeStart() {
    return this.heightPoints.filter(point => point !== this.slopeStartPoint);
  }

  get allInitialPoints() {
    return this.heightPoints.filter(point => point.isInitial);
  }

  get backendObjects() {
    let backendObjects = [
      {
        points: this.points,
        heightPoints: this.heightPoints.map(point => point.backendObject),
        area_cf_index: this.areaCfIndex,
        id_cutout: false,
      },
    ];
    for (const cutout of this.cutouts) {
      backendObjects.push({
        points: cutout.points,
        iscutout: true,
        area_cf_index: cutout.area_cf_index,
      });
    }

    return backendObjects;
  }

  removePointHighlights() {
    this.heightPoints.forEach(point => (point.highlighted = false));
  }

  set slopeStartPoint(point) {
    this._slopeStartPoint = point;
  }

  get slopeStartPoint() {
    return this._slopeStartPoint;
  }

  set slope(slope) {
    this._slope = slope;
  }

  get slope() {
    return this._slope;
  }

  get slopeDefined() {
    return this._slopeDefined;
  }

  set slopeDefined(slopeDefined) {
    this._slopeDefined = slopeDefined;
  }

  get center() {
    const polygon = this.flattenPolygon;

    // Get the number of vertices in the polygon
    const n = polygon.vertices.length;

    // Initialize the centroid coordinates to zero
    let x = 0;
    let y = 0;

    // Loop through the vertices of the polygon
    for (let i = 0; i < n; i++) {
      // Get the current vertex and the next vertex
      const current = polygon.vertices[i];
      const next = polygon.vertices[(i + 1) % n];

      // Calculate the cross product of the current and next vertices
      const cross = current.x * next.y - next.x * current.y;

      // Add the cross product to the centroid coordinates
      x += (current.x + next.x) * cross;
      y += (current.y + next.y) * cross;
    }

    // Calculate the area of the polygon
    const area = polygon.area();

    // Divide the centroid coordinates by six times the area
    x /= 6 * area;
    y /= 6 * area;

    // Return the centroid coordinates as an object with x and y properties
    return { x, y };
  }

  set slopeUserSelection(userSelection) {
    this._slopeUserSelection = userSelection;
  }

  get slopeUserSelection() {
    return this._slopeUserSelection;
  }
}
